import React, { useEffect, useState } from 'react';
import { reservationService } from '@/application/services';
import {
  ReservationWithDetails,
  ReservationSource,
  ReservationFilters,
  ReservationsSummary,
} from '@/domain/entities';
import { ReservationsTable } from '../components/ReservationsTable';
import { ReservationsFilters as ReservationsFiltersComponent } from '../components/ReservationsFilters';
import { HttpError } from '@/domain/entities';

export const ReservationsPage: React.FC = () => {
  const [reservations, setReservations] = useState<ReservationWithDetails[]>([]);
  const [sources, setSources] = useState<ReservationSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<ReservationFilters>({ status: 'active' });

  const [summary, setSummary] = useState<ReservationsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    loadSources();
    loadSummary();
  }, []);

  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.sourceId, filters.sku, filters.orderId]);

  const loadSources = async () => {
    try {
      const data = await reservationService.getReservationSources();
      setSources(data);
    } catch (err) {
      console.error('Failed to load sources:', err);
    }
  };

  const loadReservations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reservationService.getActiveReservations(filters);
      setReservations(data || []);
    } catch (err) {
      console.error('Failed to load reservations:', err);
      setReservations([]);
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Не удалось загрузить резервы');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const data = await reservationService.getReservationsSummary();
      setSummary(data);
    } catch (err) {
      console.error('Failed to load reservations summary:', err);
      // Не показываем ошибку, просто не отображаем сводку
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: ReservationFilters) => {
    setFilters(newFilters);
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Резервы товаров</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Управление резервами товаров
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="flex-shrink-0 mt-4 border-t border-gray-200 dark:border-gray-700"></div>

      {/* Summary Cards */}
      {!summaryLoading && summary && (
        <div className="flex-shrink-0 mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Активные резервы</h3>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-2">{summary.activeCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Завершенные</h3>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-2">{summary.completedCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Отмененные</h3>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-2">{summary.cancelledCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Зарезервировано единиц</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{summary.totalReservedQuantity}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex-shrink-0 mt-6">
        <ReservationsFiltersComponent
          filters={filters}
          sources={sources}
          onChange={handleFiltersChange}
          onReset={() => setFilters({ status: 'active' })}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-2 rounded">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 flex flex-col min-h-0 mt-4">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">Загрузка...</div>
        ) : (
          <div className="flex-1 overflow-auto min-h-0">
            <ReservationsTable reservations={reservations} />
          </div>
        )}
      </div>
    </div>
  );
};

